// View-model for the Users list. Maps the backend StaffMember shape (from
// /api/v1/staff) into the row shape the User Management UI renders.

import type { ProvisioningStatus, UserAction } from '@bonsai/shared';

import type { StaffMember } from '../../../api/staff';

import { ROLE_DEFS, type UserRoleId, type UserStatus } from './roleTaxonomy';

export interface UserRegionRef {
  id: string;
  name: string;
  code: string;
}

export interface UserRow {
  id: string;
  name: string;
  email: string | null;
  /** Auth0 role ids this user holds (BON-105). Empty when the user has no
   *  Auth0 account yet or holds no configured roles. */
  roles: UserRoleId[];
  regions: UserRegionRef[];
  /** What the pill shows: `inactive` wins over the provisioning status. */
  status: UserStatus;
  /** The backend lifecycle state, which survives deactivation. */
  provisioningStatus: ProvisioningStatus;
  invitedAt: string | null;
  hasAuth0: boolean;
  isStaff: boolean;
  /** Count of upcoming workshop assignments — the offboarding "impact". */
  upcomingWorkshops: number;
  fareharborUsername: string | null;
  hireDate: string | null;
  /** What the viewer may do to this row, as decided by the API. */
  allowedActions: UserAction[];
}

export function canTake(row: Pick<UserRow, 'allowedActions'>, action: UserAction): boolean {
  return row.allowedActions.includes(action);
}

/** A backend role string may arrive in various shapes; normalize to our
 *  taxonomy id where it matches, otherwise drop. */
function normalizeRole(raw: string | null | undefined): UserRoleId | undefined {
  if (!raw) return undefined;
  const slug = raw
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');
  return ROLE_DEFS.find((r) => r.id === slug)?.id;
}

function deriveStatus(s: StaffMember): UserStatus {
  if (!s.isActive) return 'inactive';
  return s.provisioningStatus;
}

export function toUserRow(s: StaffMember): UserRow {
  return {
    id: s.id,
    name: s.name || `${s.firstName} ${s.lastName}`.trim(),
    email: s.email,
    roles: (s.roles ?? []).map(normalizeRole).filter((r): r is UserRoleId => r !== undefined),
    regions: (s.regions ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      code: r.code ?? r.name,
    })),
    status: deriveStatus(s),
    provisioningStatus: s.provisioningStatus,
    invitedAt: s.invitedAt,
    hasAuth0: s.hasAuth0,
    isStaff: true,
    upcomingWorkshops: s.upcomingWorkshops ?? 0,
    fareharborUsername: s.fareharborUsername ?? null,
    hireDate: s.hireDate ?? null,
    allowedActions: s.allowedActions,
  };
}

/** Extract the surname for sort purposes. Splits the display name on
 *  whitespace and treats the last token as the family name — good enough
 *  for staff records with "First Last" / "First Middle Last" shapes;
 *  doesn't handle compound surnames (de la Cruz) perfectly. Falls back
 *  to the whole name when there's only one token. */
function lastNameKey(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  return parts[parts.length - 1].toLowerCase();
}

/** Sort by last name, alphabetical. Status no longer groups the list — the
 *  status pill in each row carries that information. */
export function sortUserRows(rows: UserRow[]): UserRow[] {
  return [...rows].sort((a, b) => {
    const cmp = lastNameKey(a.name).localeCompare(lastNameKey(b.name));
    if (cmp !== 0) return cmp;
    // Same surname → tiebreak by full name for stability.
    return a.name.localeCompare(b.name);
  });
}
